/**
 * useRodeoEvents — Convenience hooks for event data in context of a rodeo/jackpot.
 *
 * Wraps generated hooks to encapsulate filter logic used by JackpotDetail,
 * RodeoDetail, and EventDetail pages.
 */

import { useMemo } from 'react';
import { useEventsList, useEvents } from '../generated_hooks/generated-events-hooks';
import { useMyRegistrations } from './useRegistrationOps';

/**
 * Fetch all events for a specific rodeo/jackpot.
 */
export function useRodeoEvents(rodeoId: string) {
  return useEventsList(
    rodeoId
      ? { filters: { rodeo_id: rodeoId } }
      : undefined,
  );
}

/**
 * Fetch a single event by ID using the generated hook.
 */
export function useEventDetail(eventId: string) {
  return useEvents(eventId);
}

/**
 * Get the user's registration for a specific event.
 * Returns the registration object or null.
 */
export function useUserRegistrationForEvent(eventId: string) {
  const { data: registrations, ...rest } = useMyRegistrations({ event_id: eventId });

  const registration = useMemo(() => {
    if (!registrations || !Array.isArray(registrations)) return null;
    return registrations.find((r: any) => r.event_id === eventId) ?? null;
  }, [registrations, eventId]);

  return { data: registration, ...rest };
}
